import { useEffect, useRef, useState, useCallback } from "react";
import { MapPin, MoveHorizontal } from "lucide-react";
import { BEFORE_AFTER } from "@/lib/siteData";

const ComparisonSlider = ({ item, index }) => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);

  const updatePos = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setPos((x / rect.width) * 100);
  }, []);

  const onPointerDown = (e) => {
    setDragging(true);
    updatePos(e.clientX);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    updatePos(e.clientX);
  };
  const onPointerUp = () => setDragging(false);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => updatePos(e.clientX);
    const onUp = () => setDragging(false);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [dragging, updatePos]);

  return (
    <article
      data-testid={`before-after-card-${item.id}`}
      className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden"
    >
      <div
        ref={containerRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className="relative aspect-square sm:aspect-[4/3] select-none touch-none bg-green-100 cursor-ew-resize overflow-hidden"
        data-testid={`before-after-slider-${item.id}`}
      >
        <img
          src={item.after}
          alt={`${item.title} — after`}
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />

        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        >
          <img
            src={item.before}
            alt={`${item.title} — before`}
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
        </div>

        <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-green-950/80 text-white text-xs font-bold uppercase tracking-widest backdrop-blur-sm">
          Before
        </div>
        <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-green-600/95 text-white text-xs font-bold uppercase tracking-widest backdrop-blur-sm">
          After
        </div>

        <div
          className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_20px_rgba(0,0,0,0.4)] pointer-events-none"
          style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
        />
        <button
          type="button"
          data-testid={`before-after-handle-${item.id}`}
          aria-label="Drag to compare"
          onPointerDown={onPointerDown}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-white text-green-700 shadow-xl flex items-center justify-center ring-2 ring-green-700/20 hover:scale-110 transition-transform"
          style={{ left: `${pos}%` }}
        >
          <MoveHorizontal className="w-5 h-5" />
        </button>
      </div>

      <div className="p-5 sm:p-6">
        <div className="text-xs text-slate-400 font-mono mb-1">PAIR 0{index + 1}</div>
        <h3
          className="text-lg sm:text-xl font-bold text-green-950"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {item.title}
        </h3>
        <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
          <MapPin className="w-3 h-3" /> {item.location}
        </div>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">{item.description}</p>
      </div>
    </article>
  );
};

export const BeforeAfter = () => {
  return (
    <section
      id="before-after"
      data-testid="before-after-section"
      className="py-20 sm:py-28 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="inline-block text-green-700 font-semibold text-sm uppercase tracking-widest">
              Before &amp; after
            </span>
            <h2
              data-testid="before-after-headline"
              className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-green-950 tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Drag the slider. See the difference.
            </h2>
          </div>
          <p className="text-slate-600 max-w-md text-base lg:text-lg">
            Real transformations — slide each photo to reveal how the pond or garden
            looked before we got to work.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {BEFORE_AFTER.map((b, i) => (
            <ComparisonSlider key={b.id} item={b} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BeforeAfter;
